import type { CSSProperties } from 'react';
import { ExperienceDisclaimer } from '../components/ExperienceDisclaimer';
import { getReportDetail } from '../data/report-details';
import { getResultCoverConfig } from '../data/result-cover-config';
import type { Hero } from '../types';

type Props = {
  hero: Hero;
  onRestart: () => void;
  onBackToCover: () => void;
  onMoreTests: () => void;
};

const metricIcons = ['heart', 'shield', 'emotion', 'flame'] as const;
const unifiedReportHeroIds = new Set(Array.from({ length: 25 }, (_, index) => index + 1));

function MetricIcon({ kind }: { kind: (typeof metricIcons)[number] }) {
  if (kind === 'heart') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 38S8 29 8 17.5C8 11.7 15.3 8.2 20 13c1.6 1.6 2.8 3.5 4 5.7 1.2-2.2 2.4-4.1 4-5.7 4.7-4.8 12-1.3 12 4.5C40 29 24 38 24 38Z" /></svg>;
  }
  if (kind === 'shield') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 6 39 12v10c0 9.7-6.2 16.7-15 20-8.8-3.3-15-10.3-15-20V12l15-6Z" /><path d="m17 24 5 5 10-11" /></svg>;
  }
  if (kind === 'emotion') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 27c7-2 8-14 17-14 7 0 11 5 11 11 0 9-8 15-16 15-7 0-12-4-12-12Z" /><path d="M17 24c2 3 4 4 7 4s6-2 8-5M20 20h.1M30 19h.1" /></svg>;
  }
  return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M27 5c2 9-6 11-2 18 1-5 5-7 8-11 6 8 8 14 5 21-3 7-9 10-15 10-8 0-14-5-14-13 0-8 5-13 10-19 0 7 2 9 4 11-1-7 2-12 4-17Z" /></svg>;
}

export function ReportPage({ hero, onRestart, onBackToCover, onMoreTests }: Props) {
  const detail = getReportDetail(hero);
  const artStyle = { '--section-art': `url("${hero.result_bg}")` } as CSSProperties;
  const isUnifiedReport = unifiedReportHeroIds.has(hero.id);
  const coverConfig = getResultCoverConfig(hero);

  return (
    <section className={`report-continuation report-hero-${hero.id} ${isUnifiedReport ? 'unified-report' : ''}`} id="report" aria-labelledby="report-title">
      <article className="report-scroll">
        <header className="report-heading">
          {isUnifiedReport ? <div className="report-heading-art" aria-hidden="true" /> : null}
          <div className="report-heading-copy">
            {isUnifiedReport ? <span className="report-heading-kicker">你的盖世英雄</span> : null}
            <h2 id="report-title">
              {isUnifiedReport && coverConfig.titleAsset ? (
                <>
                  <span className="sr-only">{hero.name}</span>
                  <img className="report-name-art" src={coverConfig.titleAsset} alt="" />
                </>
              ) : <span className="report-name">{hero.name}</span>}
              <span>完整关系剧本</span>
            </h2>
            <strong>{hero.relation_type}</strong>
          </div>
        </header>

        <section className="report-panel trait-panel" aria-labelledby="trait-title">
          <h3 id="trait-title"><span>✿</span>核心特质解析<span>✿</span></h3>
          <div className="report-metrics">
            {Object.entries(hero.front_cards).map(([label, value], index) => (
              <div className="report-metric" key={label}>
                <div className="metric-emblem"><MetricIcon kind={metricIcons[index] ?? 'heart'} /></div>
                <div className="metric-copy"><span>{label}</span><strong>{value}</strong></div>
                <p className="metric-note">{detail.metricNotes[label]}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relationship-report" aria-labelledby="depth-title">
          <h3 id="depth-title"><span>✿</span>你们的关系深度报告<span>✿</span></h3>
          {detail.sections.map((section, index) => {
            const isLongCopy = section.lines.length >= 4 || section.lines.join('').length >= 120;
            return (
              <article
                className={`report-story ${index % 2 === 0 ? 'visual-left' : 'visual-right'} ${isLongCopy ? 'long-copy' : ''}`}
                key={section.title}
                style={artStyle}
              >
                <div className="story-visual" aria-hidden="true"><span>{section.symbol}</span></div>
                <div className="story-copy">
                  <h4>{section.title}</h4>
                  {section.lines.map((line) => <p key={line}>{line}</p>)}
                </div>
              </article>
            );
          })}
        </section>

        <section className="destiny-quote">
          <p><span>✦</span>宿命台词<span>✦</span></p>
          <blockquote>“{detail.destinyLine}”</blockquote>
        </section>

        <button className="report-more-tests-button" type="button" onClick={onMoreTests}>
          <span aria-hidden="true">✿</span>
          <strong>带图好评，获取更多趣味测试</strong>
          <span aria-hidden="true">✿</span>
        </button>

        <div className="report-utility-actions">
          <button type="button" onClick={onBackToCover}>返回结果首页</button>
          <span aria-hidden="true">·</span>
          <button type="button" onClick={onRestart}>重新测试</button>
        </div>
        <ExperienceDisclaimer className="report-disclaimer" />
      </article>
    </section>
  );
}
